"use client";

import { create } from "zustand";
import { useQuery } from "@tanstack/react-query";
import { useCustomerConfig } from "./use-customer-config";
import { gql } from "graphql-request";
import { createGraphQLClient } from "@/services/graphql.service";
import { useEffect } from "react";
import { useEntitiesFromDb } from "./schema/use-entities-from-db";
import { resolveFeatureEntity } from "@/services/template.service";

const availableLanguagesQuery = gql`
  query AvailableLanguages {
    languageList(pagination: { page: 1, per_page: 10000 }) {
      data {
        id
        name
        locale
      }
    }
  }
`;

interface AvailableLanguagesResponse {
  languageList: {
    data: Language[];
  };
}

export interface Language {
  id: string;
  name: string;
  locale: string;
}

interface LanguageStore {
  selectedLanguage: Language | null;
  setSelectedLanguage: (language: Language) => void;
}

const useLanguageStore = create<LanguageStore>((set) => ({
  selectedLanguage: null,
  setSelectedLanguage: (language) => set({ selectedLanguage: language }),
}));

export const useLanguages = () => {
  const { selectedLanguage, setSelectedLanguage } = useLanguageStore();
  const { defaultLanguage } = useCustomerConfig();
  const { entities } = useEntitiesFromDb();

  const languageEntityName = resolveFeatureEntity("Language", entities)?.name;

  // Middleware guarantees auth
  const { data: languages, isLoading } = useQuery({
    queryKey: ["languages", languageEntityName],
    queryFn: async () => {
      const client = createGraphQLClient({
        endpointName: "availableLanguages",
      });
      const response = await client.request<AvailableLanguagesResponse>(availableLanguagesQuery);
      return response.languageList.data;
    },
    // Opt-in feature: without the entity there is no `languageList` to query.
    enabled: !!languageEntityName,
    // TODO: Invalidate on language changes from other users (e.g., via websocket event)
    staleTime: 20 * 60 * 1000, // 20 minutes
  });

  useEffect(() => {
    // Set default language if none selected
    if (!selectedLanguage && languages?.length) {
      const res = languages.find((lang) => lang.id === defaultLanguage?.id) || languages[0];
      setSelectedLanguage(res);
    }
  }, [selectedLanguage, setSelectedLanguage, languages, defaultLanguage]);

  return {
    languages: languages || [],
    selectedLanguage,
    setSelectedLanguage,
    isLoading,
  };
};
